import { useTranslate } from '@tolgee/react';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { relocatePublishButtons } from '../../utils/uploadMediaFile';
import {
  ALL,
  FACEBOOK,
  PLATFORM_LIST,
  PLATFORM_LIST_NAMES,
  PLATFORM_LIST_NAMES_WITH_ALL
} from '../../../../config/constants';
import { useDispatch, useSelector } from 'react-redux';
import { lomavisCreatorRootState } from '../../../../pages/lomavisCreator';
import { PLATFORM_COLORS, PLATFORM_ICONS } from '../../../../config/icons';

import SuccessToast from '../../cards/toast';
import { AddImageButton, EditorHeader } from '../sharedComponents';
import toast from 'react-hot-toast';
import { IMAGE, VIDEO } from '../../config/constants';
import MediaTags from './components/mediaTags';
import axiosClient from '../../../../utils/axios';
import {
  setMediaDataAttribute,
  setModalUiContent
} from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import { lomavisShowToastr } from '../../../../utils/ui/toastr';
import EditorTitle from '../commonElements/editorTitle';

interface Props {
  handleClose: Function;
  handleCloseNoConf: Function;
  currentPlatform: string;
  currentIndex: any;
}

const MediaAltTags: FC<Props> = ({
  handleClose,
  handleCloseNoConf,
  currentPlatform,
  currentIndex
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const footerRef = useRef(null);

  const [rightMargin, setRightMargin] = useState(0);
  const [startSubmission, setStartSubmission] = useState(false);
  const [showPlatformDropdown, setShowPlatformDropdown] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0);

  const triggerSuccessToast = (type) => {
    toast.success(
      type === IMAGE
        ? t('lomavisCreator.image.saved')
        : t('lomavisCreator.video.saved'),
      {
        position: 'top-right',
        id: 'accounts.invites.table.invite_deleted'
      }
    );
  };

  const platformEnabled = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.platformEnabled
  );
  const allEnabledPlatforms = PLATFORM_LIST.filter(
    (item) => platformEnabled[item] === true
  );
  const mediaAltTagsStates = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.mediaAltTags
  );
  const [selectedPlatform, setSelectedPlatform] = useState(
    currentPlatform ? currentPlatform : allEnabledPlatforms[0]
  );
  const postDataMediaFiles = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[selectedPlatform]?.media
  );

  const mediaFiles = postDataMediaFiles?.map((file: any) => {
    return {
      id: `${file.id}`,
      uuid: file.uuid,
      name: file.name,
      preview_image: file.preview_image,
      orientation: file.orientation,
      media_type: file.media_type,
      video_duration: file.video_duration,
      uploadPreset: file.uploadPreset,
      file: file.file,
      file_type: file.file_type,
      uploaded: file.uploaded,
      assetBlob: file.assetBlob,
      editorUpdateState: file.editorUpdateState,
      source_media_uuid: file.source_media_uuid,
      key: `${new Date()}`,
      previewImageURL: file.previewImageURL,
      thumbnail_image: file.thumbnail_image,
      original_asset: file.original_asset,
      media_alt_description: file.media_alt_description
    };
  });

  const [mediaAltTags, setMediaAltTags] = useState([]);

  useEffect(() => {
    if (!postDataMediaFiles) return;

    const altTags = postDataMediaFiles.map((file: any) => ({
      uuid: file?.uuid,
      preview_image: file.preview_image,
      media_type: file.media_type,
      media_alt_description: file?.media_alt_description || '',
      api_url: file?.api_url || ''
    }));

    setMediaAltTags(altTags);
  }, [postDataMediaFiles]);

  const [selectedIndex, setSelectedIndex] = useState(currentIndex || 0);

  useEffect(() => {
    relocatePublishButtons({
      footerRef: footerRef,
      setRightMargin: setRightMargin
    });
  }, []);

  const hasMounted = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) {
      // Skip the first render
      hasMounted.current = true;
      return;
    }

    // Only run this on subsequent changes
    setSelectedIndex(0);
  }, [selectedPlatform]);

  console.log('platforms', [currentPlatform, ...allEnabledPlatforms]);

  const EnabledPlatforms =
    currentPlatform === ALL
      ? []
      : Array.from(new Set([currentPlatform, ...allEnabledPlatforms]));

  const getChangedAltTags = () => {
    return mediaAltTags.filter((tag) => {
      const original = postDataMediaFiles.find(
        (file) => file.uuid === tag.uuid
      );
      return (
        original &&
        tag.media_alt_description.trim() !==
          (original.media_alt_description || '').trim()
      );
    });
  };

  const handleSave = async (continueEdit) => {
    const changedTags = getChangedAltTags();

    if (changedTags.length === 0) {
      lomavisShowToastr(
        t('lomavisCreator.mediaAltTags.success.save'),
        'success'
      );
      setStartSubmission(false);
      !continueEdit && handleClose();
      return;
    }

    !continueEdit && setStartSubmission(true);

    try {
      await Promise.all(
        changedTags.map(async (tag) => {
          const trimmedValue = tag.media_alt_description.trim();

          // PATCH request
          await axiosClient.patch(tag.api_url, {
            media_alt_description: trimmedValue
          });

          // Redux update
          dispatch(
            setMediaDataAttribute({
              platform: selectedPlatform,
              uuid: tag.uuid,
              attribute: 'media_alt_description',
              value: tag.media_alt_description
            })
          );
        })
      );

      lomavisShowToastr(
        t('lomavisCreator.mediaAltTags.success.save'),
        'success'
      );
    } catch (error) {
      console.error('Save error:', error);
      lomavisShowToastr(t('lomavisCreator.mediaAltTags.error.save'), 'error');
    } finally {
      setStartSubmission(false);
      !continueEdit && handleCloseNoConf();
    }
  };

  useEffect(() => {
    const hasUnsavedChanges = getChangedAltTags().length > 0;
    if (hasUnsavedChanges && mediaAltTagsStates?.allowClose) {
      dispatch(
        setModalUiContent({
          modal: 'mediaAltTags',
          attribute: 'allowClose',
          value: false
        })
      );
    } else if (!hasUnsavedChanges && !mediaAltTagsStates?.allowClose) {
      dispatch(
        setModalUiContent({
          modal: 'mediaAltTags',
          attribute: 'allowClose',
          value: true
        })
      );
    }
  }, [mediaAltTags]);

  return (
    <div className="d-flex flex-column w-100 h-100 gap-4">
      <SuccessToast show={showToast} onClose={() => setShowToast(false)} />

      <EditorHeader>
        <div
          style={{}}
          className="d-flex flex-fill align-items-center h-100 main_editor"
        >
          <EditorTitle title={'mediaAltTags'} toolTip={true} />
        </div>
      </EditorHeader>

      <div
        key={refetchKey}
        style={{ overflowY: 'auto' }}
        className="d-flex w-100 flex-column flex-grow-1 flex-fill bg-white rounded-3 p-1"
      >
        <EditorHeader>
          <div
            style={{}}
            className="d-flex justify-content-between align-items-center flex-fill h-100 main_editor"
          >
            <Dropdown
              onClick={() => setShowPlatformDropdown(!showPlatformDropdown)}
              show={showPlatformDropdown}
              className=""
            >
              <div
                id="dropdown-basic"
                className={`bg-white d-flex align-items-center ${
                  EnabledPlatforms?.length > 1
                    ? 'dropdown-toggle cursor-pointer'
                    : ''
                }`}
              >
                <i
                  className={PLATFORM_ICONS[selectedPlatform] + ' fs-1 me-2'}
                  style={{ color: PLATFORM_COLORS[selectedPlatform] }}
                ></i>

                <div className="fs-3 text-dark">
                  {PLATFORM_LIST_NAMES_WITH_ALL?.[selectedPlatform]}
                </div>
              </div>

              <Dropdown.Menu className="bg-white">
                {EnabledPlatforms?.map((platform, index) => (
                  <Dropdown.Item
                    key={index}
                    id={platform + 'key'}
                    onClick={() => setSelectedPlatform(platform)}
                    className="w-100 d-flex justify-content-start align-items-center gap-2 bg-white"
                  >
                    <i
                      className={PLATFORM_ICONS[platform] + ' fs-2'}
                      style={{ color: PLATFORM_COLORS[platform] }}
                    ></i>
                    <div className="fs-3 text-dark">
                      {PLATFORM_LIST_NAMES_WITH_ALL?.[platform]}
                    </div>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </EditorHeader>
        <MediaTags
          PLATFORM={currentPlatform}
          mediaFiles={mediaFiles}
          mediaAltTags={mediaAltTags}
          setMediaAltTags={setMediaAltTags}
        />
      </div>

      <div
        ref={footerRef}
        style={{ bottom: '0', zIndex: 100 }}
        className="w-100 d-flex justify-content-end gap-3 align-items-center bg-white py-2 border-top h-60px bg-white rounded-3"
      >
        <Button
          onClick={() => {
            handleClose();
          }}
          variant="secondary"
          size="sm"
        >
          {t('lomavis_creator.cancel')}
        </Button>

        <Button variant="secondary" size="sm" onClick={() => handleSave(true)}>
          {startSubmission
            ? t('lomavis_creator.saving')
            : t('lomavis_creator.saveAndContinue')}
        </Button>

        <Button
          style={{ marginRight: rightMargin + 'px' }}
          variant="primary"
          size="sm"
          onClick={() => handleSave(false)}
        >
          {startSubmission && (
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
          )}
          {startSubmission ? t('lomavis_creator.saving') : t('save')}
        </Button>
      </div>
    </div>
  );
};

export default MediaAltTags;
